import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import HeadData from 'components/Common/HeadData'
import FaqStructuredData from 'components/Content/FrequentlyAskedQuestions/FaqStructuredData'
import { getImageUrl } from 'lib/image/imageUtils'
import LeLiveHero from 'leLive/components/LeLiveHero'
import LeLiveCountdown from 'leLive/components/LeLiveCountdown'
import LeLivePrizes from 'leLive/components/LeLivePrizes'
import LeLiveHighlights from 'leLive/components/LeLiveHighlights'
import LeLiveVipLounge from 'leLive/components/LeLiveVipLounge'
import LeLivePartners from 'leLive/components/LeLivePartners'
import LeLiveEventDetails from 'leLive/components/LeLiveEventDetails'
import LeLiveCreditCard from 'leLive/components/LeLiveCreditCard'
import LeLiveFaqs, { getLeLiveFaqs } from 'leLive/components/LeLiveFaqs'
import { registerForLeLive, updateLeLiveRegistration, getMyRegistrations } from 'leLive/api/leLive'
import type { LeLiveRegistration } from 'leLive/api/leLive'
import type { RegistrationFormValues } from 'leLive/components/LeLiveRegistrationForm'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { trackClientEvent } from 'analytics/analytics'

const LE_LIVE_CANONICAL_PATH = '/au/live'
const LE_LIVE_EVENT_NAME = 'Luxury Escapes Live 2026'
const LE_LIVE_IMAGE_ID = 'egpqrc4p9s3oyi84eju6'
const LE_LIVE_START_DATE = '2026-05-16T10:00:00+10:00'
const LE_LIVE_END_DATE = '2026-05-17T18:00:00+10:00'

const REGISTRATION_EVENT_DAY_CATEGORY: Record<RegistrationFormValues['eventDay'], string> = {
  saturday: 'Sat, 16 May',
  sunday: 'Sun, 17 May',
}

function LeLiveLandingPage() {
  const intl = useIntl()
  const loggedIn = useAppSelector(selectLoggedIn)
  const customerPortalURL = useAppSelector((state) => state.system.customerPortalURL)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(loggedIn)
  const [registration, setRegistration] = useState<LeLiveRegistration | undefined>(undefined)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)
  const pageTitle = intl.formatMessage({
    id: '6vQIfyLHiVuFaDzZxYiE',
    defaultMessage: 'Luxury Escapes Live 2026 — Australia’s Ultimate Travel Event',
    description: 'SEO page title for the LE Live event landing page shown in browser tabs and search results',
  })
  const pageDescription = intl.formatMessage({
    id: 'pSmFKk4g-l3NJEAUIMyL',
    defaultMessage: 'Join 10,000+ travellers at Luxury Escapes Live, 16–17 May 2026 at the Royal Exhibition Building, Melbourne. Win thousands in travel prizes. Free entry for members.',
    description: 'Page meta description for LE Live landing page (SEO)',
  })
  const faqs = useMemo(() => getLeLiveFaqs(intl), [intl])
  const eventStructuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: LE_LIVE_EVENT_NAME,
    description: pageDescription,
    startDate: LE_LIVE_START_DATE,
    endDate: LE_LIVE_END_DATE,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: [getImageUrl(LE_LIVE_IMAGE_ID, { width: 1200, format: 'jpeg' })],
    url: `${customerPortalURL}${LE_LIVE_CANONICAL_PATH}`,
    location: {
      '@type': 'Place',
      name: 'Royal Exhibition Building',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '9 Nicholson St',
        addressLocality: 'Carlton',
        addressRegion: 'VIC',
        postalCode: '3053',
        addressCountry: 'AU',
      },
    },
    organizer: {
      '@type': 'Organization',
      name: 'Luxury Escapes',
      url: customerPortalURL,
    },
  }), [customerPortalURL, pageDescription])

  useEffect(() => {
    if (!loggedIn) {
      setIsLoadingRegistrations(false)
      return
    }

    let cancelled = false
    setIsLoadingRegistrations(true)
    getMyRegistrations().then((result) => {
      if (cancelled) return
      if (result.length > 0) {
        setRegistration(result[0])
      }
    }).catch(() => {
      // silently ignore — user can still register
    }).finally(() => {
      if (!cancelled) setIsLoadingRegistrations(false)
    })

    return () => { cancelled = true }
  }, [loggedIn])

  const handleRegistrationSubmit = useCallback(async(values: RegistrationFormValues) => {
    setIsSubmitting(true)
    setSubmitError(undefined)
    try {
      const hasExistingRegistration = !!registration
      const payload = {
        ticketQuantity: values.ticketQuantity,
        eventDay: values.eventDay,
        marketingOptIn: values.marketingOptIn,
      }
      const result = hasExistingRegistration
        ? await updateLeLiveRegistration(payload)
        : await registerForLeLive(payload)
      setRegistration(result)

      if (!hasExistingRegistration) {
        trackClientEvent({
          subject: 'le_live_registration',
          category: REGISTRATION_EVENT_DAY_CATEGORY[values.eventDay],
          type: 'interaction',
          action: String(values.ticketQuantity),
        })
      }
    } catch(error: unknown) {
      const statusCode = (error as Record<string, unknown>)?.statusCode
      if (statusCode === 409 && !registration) {
        try {
          const existing = await getMyRegistrations()
          if (existing.length > 0) {
            setRegistration(existing[0])
            return
          }
        } catch { /* fall through to generic error */ }
      }
      const message = 'Registration failed. Please try again.'
      setSubmitError(message)
      throw new Error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [registration])

  const hasRegistration = isLoadingRegistrations || !!registration

  return (
    <>
      <HeadData
        title={pageTitle}
        description={pageDescription}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(eventStructuredData)}
        </script>
      </Helmet>
      <FaqStructuredData items={faqs} />

      <main>
        <LeLiveHero
          isSubmitting={isSubmitting}
          isLoadingRegistrations={isLoadingRegistrations}
          registration={registration}
          submitError={submitError}
          onRegistrationSubmit={handleRegistrationSubmit}
        />
        <LeLiveCountdown />
        <LeLivePrizes hasRegistration={hasRegistration} />
        <LeLiveHighlights hasRegistration={hasRegistration} />
        <LeLiveVipLounge />
        <LeLivePartners hasRegistration={hasRegistration} />
        <LeLiveEventDetails hasRegistration={hasRegistration} />
        <LeLiveCreditCard />
        <LeLiveFaqs />
      </main>
    </>
  )
}

export default LeLiveLandingPage
